import cx from 'clsx';
import React, { Fragment, FunctionComponent } from 'react';
import styles from './Article.module.scss';
import typographyStyles from 'styles/global/typography.module.scss';
import { getArticleTerms, getDate } from 'utils';
import { WPSimpleArticle } from 'types/articles';
import { Block, BlockContent, LinkWrapper, Typography } from 'components/elements';
import { ContactForm, DisqusComments } from 'components/containers';
import { CustomArticleSlugTypes, RegularPostTypes } from 'types/global';
import { WPAcfBlog, WPAcfGlobalBlogPost } from 'types/acf';
import { usePages, usePosts } from 'hooks';
import { WPSimplePage } from 'types/pages';
import PostItems from 'components/containers/PostItems/PostItems';
import { OnMobileOnly, OnTabletAndDesktopOnly } from 'utils/breakpoints';
import { FormattedText } from '..';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';
import { useRouteTranslations } from 'utils/languages';
import DOMPurify from 'dompurify';
import Sidebar from '../Sidebar/Sidebar';

interface OwnProps {
  data: WPSimpleArticle<WPAcfBlog>;
  preview?: boolean;
}

type ArticleProps = JSX.IntrinsicElements['div'] & OwnProps;

const Article: FunctionComponent<ArticleProps> = props => {
  const POSTS_PER_PAGE = 3;
  const { formatMessage } = useIntl();
  const { translateSlug } = useRouteTranslations();
  const GLOBAL_PAGE_SLUG_TYPE: CustomArticleSlugTypes = 'global_blog_post';
  const { data: page } = usePages<WPSimplePage<WPAcfGlobalBlogPost>>({ slug: GLOBAL_PAGE_SLUG_TYPE });
  const { data: article, preview = false } = props;
  const { id, title, content, _embedded, date, slug } = article;
  const terms = getArticleTerms(_embedded);
  const { category: categories = [] } = terms || {};
  const firstCategory = categories.find(category => category.name);
  const { info } = article?.acf;
  const [globalPageOptions] = page || [];
  const { acf } = globalPageOptions || {};

  const { data: relatedArticles = [] } = usePosts({
    ...(firstCategory && { category_name: firstCategory.slug as RegularPostTypes }),
  });

  // @ts-ignore
  const filteredArticles = relatedArticles.filter(article => article.id !== id);

  const articleTitleClassName = cx({
    [typographyStyles.neue_semibold_72]: true,
    [styles.title]: true,
  });

  const renderSideContent = (article: WPSimpleArticle<WPAcfBlog>) => (
    <div className={styles.sideContent}>
      <div>
        <Sidebar data={article} />
      </div>
    </div>
  );

  return (
    <Fragment>
      <Block id="article" data-module="article">
        <BlockContent direction="row" className={styles.container}>
          <div className={styles.article}>
            {info && info.time && (
              <Typography mobile="new_neue_20_h2" tablet="new_neue_20_h2" desktop="new_neue_20_h2" as="p">
                {`${formatMessage(messages.article_readTime)} ${info.time} ${formatMessage(messages.article_minutes)}`}
              </Typography>
            )}
            <Typography
              className={articleTitleClassName}
              mobile="new_neue_semibold_26"
              tablet="new_neue_semibold_26"
              desktop="new_neue_32_h1"
              as="h1"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title.rendered) }}
            />
            {categories && categories.length && (
              <Typography className={styles.category} mobile="new_neue_20_h2" tablet="new_neue_20_h2" desktop="new_neue_20_h2" as="div">
                {categories.map((category, index) => (
                  <LinkWrapper key={`category-link-item-${category.slug}`} href={`/${translateSlug('blog')}/?category=${category.slug}`}>
                    {`${category.name}${index < categories.length - 1 ? ', ' : ''}`}
                  </LinkWrapper>
                ))}
              </Typography>
            )}
            <OnMobileOnly>{renderSideContent(article)}</OnMobileOnly>
            <FormattedText
              className={styles.content}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  content.rendered.replaceAll('table_of_contents', formatMessage(messages.table_of_contents)).replaceAll(`${slug}/`, `${slug}`),
                ),
              }}
            />
            {date && (
              <div>
                <Typography mobile="new_ibm_regular_16" tablet="new_ibm_regular_16" desktop="new_ibm_regular_16" as="span">
                  {formatMessage(messages.publication_date)}
                </Typography>
                <Typography mobile="new_ibm_bold_16" tablet="new_ibm_bold_16" desktop="new_ibm_bold_16" as="span">
                  {getDate(date)}
                </Typography>
              </div>
            )}
          </div>
          <OnTabletAndDesktopOnly>{renderSideContent(article)}</OnTabletAndDesktopOnly>
        </BlockContent>
      </Block>
      {!preview && <DisqusComments article={article} />}
      {!preview && filteredArticles && (
        <PostItems
          header={formatMessage(messages.recommended)}
          mobile={1}
          tablet={3}
          desktop={3}
          category={translateSlug('blog')}
          posts={filteredArticles}
          linkLabel={acf?.blogPosts?.buttonLabel}
          showExcerpt={true}
          length={POSTS_PER_PAGE}
        />
      )}
      {!preview && acf?.newsletterForm && <ContactForm theme="white" id="newsletter-form" name="newsletter-form" data={acf.newsletterForm} />}
    </Fragment>
  );
};

export default Article;
